import { throttle } from "lodash";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

interface UseChatMessageListScrollHandlerOptions {
  isInitialized: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onReachEnd?: () => void;
}

/**
 * Handles scrolling behavior for the chat message list:
 * - Instant scroll to the bottom once the chat is initialized
 * - Call `onReachEnd` whenever user reaches the end of the list (useful for infinite scroll mechanisms)
 */
export const useChatMessageListScrollHandler = ({
  isInitialized,
  containerRef,
  onReachEnd,
}: UseChatMessageListScrollHandlerOptions) => {
  const hasInitialScrolledRef = useRef(false);

  // One-time instant scroll to the bottom once the first batch of messages has loaded
  useLayoutEffect(() => {
    if (!isInitialized || hasInitialScrolledRef.current) {
      return;
    }

    hasInitialScrolledRef.current = true;

    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "instant",
    });
  }, [containerRef, isInitialized]);

  const handleScrollThrottled = useMemo(
    () =>
      throttle((event: Event) => {
        const container = event.target as HTMLElement | null;

        if (container && container.scrollTop < 200) {
          onReachEnd?.();
        }
      }, 500),
    [onReachEnd]
  );

  // Call `onReachEnd` whenever the user scrolls to within 200px of the top of the list
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    container.addEventListener("scroll", handleScrollThrottled);

    return () => {
      container.removeEventListener("scroll", handleScrollThrottled);
      handleScrollThrottled.flush();
    };
  }, [onReachEnd, handleScrollThrottled, containerRef]);
};
